import { create } from 'zustand'
import type { Checkup, CheckupCreate, CheckupUpdate } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface CheckupsState {
  checkups: Checkup[]
  upcomingCheckups: Checkup[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchCheckupsByPet: (petId: string) => Promise<void>
  fetchUpcomingCheckups: () => Promise<void>
  createCheckup: (petId: string, data: CheckupCreate) => Promise<Checkup>
  updateCheckup: (id: string, data: CheckupUpdate) => Promise<Checkup>
  deleteCheckup: (id: string) => Promise<void>
  clearError: () => void
  clearCheckups: () => void
}

export const useCheckupsStore = create<CheckupsState>((set) => ({
  checkups: [],
  upcomingCheckups: [],
  isLoading: false,
  error: null,

  fetchCheckupsByPet: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ checkups: Checkup[] }>(`/api/pets/${petId}/checkups`)
      set({ checkups: response.checkups, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar controles'
      set({ error: message, isLoading: false })
    }
  },

  fetchUpcomingCheckups: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ checkups: Checkup[] }>('/api/checkups/upcoming')
      set({ upcomingCheckups: response.checkups, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar próximas citas'
      set({ error: message, isLoading: false })
    }
  },

  createCheckup: async (petId: string, data: CheckupCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ checkup: Checkup }>(`/api/pets/${petId}/checkups`, data)
      set((state) => ({
        checkups: [response.checkup, ...state.checkups],
        isLoading: false,
      }))
      return response.checkup
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al crear control'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateCheckup: async (id: string, data: CheckupUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ checkup: Checkup }>(`/api/checkups/${id}`, data)
      set((state) => ({
        checkups: state.checkups.map((c) => (c.id === id ? response.checkup : c)),
        upcomingCheckups: state.upcomingCheckups.map((c) => (c.id === id ? response.checkup : c)),
        isLoading: false,
      }))
      return response.checkup
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar control'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteCheckup: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/checkups/${id}`)
      set((state) => ({
        checkups: state.checkups.filter((c) => c.id !== id),
        upcomingCheckups: state.upcomingCheckups.filter((c) => c.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar control'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),
  clearCheckups: () => set({ checkups: [], upcomingCheckups: [] }),
}))
